import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getLeaveTypes, getMyLeaveBalance, getLeaveRequests,
  submitLeaveRequest, cancelLeaveRequest, getHolidays, getHolidayLocations,
  getTeamLeaveRequests, approveLeaveRequest, rejectLeaveRequest, getMyReferralLink,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import { LoadingOverlay } from "../components/LoadingOverlay";

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending:  { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  approved: { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  rejected: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  cancelled:{ bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" },
};

export default function EmployeePortal() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdminRole = ["admin", "hr", "platform_admin"].includes(user?.role || "");
  const [tab, setTab] = useState<"leaves" | "myteam" | "holidays">("leaves");
  const [showForm, setShowForm] = useState(false);
  const currentYear = new Date().getFullYear();
  const [locationFilter, setLocationFilter] = useState("");
  const { data: holidayLocations = [] } = useQuery({
    queryKey: ["holiday-locations"],
    queryFn: getHolidayLocations,
  });
  const { data: holidays = [] } = useQuery({
    queryKey: ["holidays", currentYear, locationFilter],
    queryFn: () => getHolidays(currentYear, locationFilter || undefined),
  });
  const [form, setForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    half_day: false,
    reason: "",
  });
  const [formError, setFormError] = useState("");

  const { data: types = [] } = useQuery({ queryKey: ["leave-types"], queryFn: getLeaveTypes });
  const { data: balances = [] } = useQuery({ queryKey: ["my-balance"], queryFn: () => getMyLeaveBalance() });
  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["my-requests"],
    queryFn: () => getLeaveRequests({ mine: true }),
  });

  const submitMut = useMutation({
    mutationFn: submitLeaveRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-requests"] });
      qc.invalidateQueries({ queryKey: ["my-balance"] });
      setShowForm(false);
      setForm({ leave_type_id: "", start_date: "", end_date: "", half_day: false, reason: "" });
      setFormError("");
    },
    onError: (e: any) => setFormError(e?.response?.data?.detail || "Failed to submit request"),
  });

  const cancelMut = useMutation({
    mutationFn: cancelLeaveRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-requests"] });
      qc.invalidateQueries({ queryKey: ["my-balance"] });
    },
  });

  const { data: teamRequests = [] } = useQuery({
    queryKey: ["leave-requests-team"],
    queryFn: getTeamLeaveRequests,
  });
  const pendingTeamCount = (teamRequests as any[]).filter((r: any) => r.status === "pending").length;

  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const approveMut = useMutation({
    mutationFn: approveLeaveRequest,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests-team"] }),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => rejectLeaveRequest(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave-requests-team"] }); setRejectId(null); setRejectReason(""); },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!form.leave_type_id || !form.start_date || !form.end_date) {
      setFormError("Please fill all required fields");
      return;
    }
    submitMut.mutate({
      leave_type_id: parseInt(form.leave_type_id),
      start_date: form.start_date,
      end_date: form.end_date,
      half_day: form.half_day,
      reason: form.reason,
    });
  };

  const [referralCopied, setReferralCopied] = useState(false);
  const { data: referralData } = useQuery({
    queryKey: ["my-referral-link"],
    queryFn: getMyReferralLink,
    retry: false,
  });

  function copyReferralLink() {
    if (referralData?.link) {
      navigator.clipboard.writeText(referralData.link).then(() => {
        setReferralCopied(true);
        setTimeout(() => setReferralCopied(false), 3000);
      });
    }
  }

  return (
    <div>
      <LoadingOverlay active={
        submitMut.isPending || cancelMut.isPending || approveMut.isPending || rejectMut.isPending
      } />
      <h1 style={{ margin: "0 0 16px", color: "#0f172a", fontSize: 24 }}>My Leave Dashboard</h1>

      {/* ── Refer a Friend banner ── */}
      {referralData?.link && (
        <div style={{
          background: "linear-gradient(135deg, #eff6ff, #f0fdf4)",
          border: "1px solid #bfdbfe", borderRadius: 12,
          padding: "14px 18px", marginBottom: 20,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1e3a8a", marginBottom: 3 }}>
              👥 Refer a Friend
            </div>
            <div style={{ fontSize: 12, color: "#475569" }}>
              Share your personal link — candidates who apply via your link are tagged as your referral.
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, fontFamily: "monospace" }}>
              {referralData.link}
            </div>
          </div>
          <button onClick={copyReferralLink} style={{
            background: referralCopied ? "#dcfce7" : "linear-gradient(135deg,#1e3a8a,#00c9a7)",
            color: referralCopied ? "#166534" : "white",
            border: referralCopied ? "1px solid #86efac" : "none",
            borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700,
            cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
          }}>
            {referralCopied ? "✓ Copied!" : "🔗 Copy My Link"}
          </button>
        </div>
      )}

      {/* Tab bar */}
      <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
        {(["leaves", ...(!isAdminRole && (pendingTeamCount > 0 || (teamRequests as any[]).length > 0) ? ["myteam"] : []), "holidays"] as const).map((t: any) => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "active" : ""}>
            {t === "leaves" ? "My Leaves" : t === "holidays" ? "Public Holidays" : (
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                My Team
                {pendingTeamCount > 0 && (
                  <span style={{ background: "#f59e0b", color: "white", borderRadius: "50%", minWidth: 16, height: 16, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
                    {pendingTeamCount}
                  </span>
                )}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── My Team tab ── */}
      {tab === "myteam" && (
        <div>
          {rejectId !== null && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
              <div style={{ background: "white", borderRadius: 12, padding: 24, width: "min(400px, calc(100vw - 32px))" }}>
                <h3 style={{ margin: "0 0 14px", color: "#1e3a8a" }}>Reject Leave Request</h3>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  placeholder="Reason (optional)"
                  style={{ width: "100%", height: 80, padding: 8, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                  <button onClick={() => setRejectId(null)} style={{ background: "#f1f5f9", color: "#334155", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Cancel</button>
                  <button onClick={() => rejectMut.mutate({ id: rejectId!, reason: rejectReason })}
                    disabled={rejectMut.isPending}
                    style={{ background: "#dc2626", color: "white", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Reject</button>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: "grid", gap: 10 }}>
            {(teamRequests as any[]).filter((r: any) => r.status === "pending").map((req: any) => (
              <div key={req.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#1e3a8a22", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, color: "#1e3a8a" }}>
                      {(req.user_name || "?")[0].toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{req.user_name}</span>
                    <span style={{ fontSize: 12, color: "#64748b" }}>· {req.leave_type}</span>
                    <span style={{ background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>pending</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b", marginLeft: 38 }}>
                    {formatDate(req.start_date)} → {formatDate(req.end_date)} · <strong>{req.days} day{req.days !== 1 ? "s" : ""}</strong>
                  </div>
                  {req.reason && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2, marginLeft: 38 }}>{req.reason}</div>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => approveMut.mutate(req.id)} disabled={approveMut.isPending} style={{ background: "#dcfce7", color: "#166534", border: "1px solid #86efac", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Approve</button>
                  <button onClick={() => setRejectId(req.id)} style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Reject</button>
                </div>
              </div>
            ))}
            {(teamRequests as any[]).filter((r: any) => r.status === "pending").length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                <div>All caught up — no pending requests from your team</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Public Holidays tab ── */}
      {tab === "holidays" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>{currentYear} public holidays</p>
            {holidayLocations.length > 0 && (
              <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)}
                style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 10px", fontSize: 13, color: "#334155" }}>
                <option value="">All offices</option>
                {holidayLocations.map((loc: string) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            )}
          </div>
          {holidays.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ display: "block", margin: "0 auto" }}>
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <div style={{ marginTop: 12 }}>No public holidays added yet.</div>
            </div>
          ) : (() => {
            const today = new Date(); today.setHours(0,0,0,0);
            const upcoming = (holidays as any[]).filter((h: any) => new Date(h.date + "T00:00:00") >= today);
            const past = (holidays as any[]).filter((h: any) => new Date(h.date + "T00:00:00") < today);
            const HolidayRow = ({ h, dimmed }: { h: any; dimmed?: boolean }) => (
              <div key={h.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "11px 16px", borderBottom: "1px solid #f1f5f9",
                opacity: dimmed ? 0.5 : 1,
              }}>
                <div style={{
                  width: 42, textAlign: "center", flexShrink: 0,
                  background: dimmed ? "#f8fafc" : "#eff6ff", borderRadius: 8, padding: "4px 0",
                }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: dimmed ? "#94a3b8" : "#1e3a8a", lineHeight: 1 }}>
                    {new Date(h.date + "T00:00:00").getDate()}
                  </div>
                  <div style={{ fontSize: 10, color: dimmed ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {new Date(h.date + "T00:00:00").toLocaleDateString("en-GB", { month: "short" })}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: dimmed ? "#94a3b8" : "#1e293b" }}>{h.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                    {new Date(h.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                  </div>
                </div>
                {h.location && (
                  <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 12, padding: "2px 8px", fontSize: 11, flexShrink: 0 }}>{h.location}</span>
                )}
              </div>
            );
            return (
              <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                {upcoming.length > 0 && (
                  <>
                    <div style={{ padding: "8px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Upcoming — {upcoming.length} holiday{upcoming.length !== 1 ? "s" : ""}
                    </div>
                    {upcoming.map((h: any) => <React.Fragment key={h.id}><HolidayRow h={h} /></React.Fragment>)}
                  </>
                )}
                {past.length > 0 && (
                  <>
                    <div style={{ padding: "8px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Past — {past.length} holiday{past.length !== 1 ? "s" : ""}
                    </div>
                    {past.map((h: any) => <React.Fragment key={h.id}><HolidayRow h={h} dimmed /></React.Fragment>)}
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Leaves tab ── */}
      {tab === "leaves" && <div>
      {/* Balance Cards */}
      <div style={{ marginBottom: 28 }}>
        <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 14 }}>
          {currentYear} leave balances
        </p>
        <div className="grid-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
          {balances.map((bal: any) => (
            <div key={bal.leave_type_id} style={{
              background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
              padding: "16px 18px", borderTop: `4px solid ${bal.color}`,
            }}>
              <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>{bal.leave_type_name}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#1e3a8a" }}>{bal.available}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                of {bal.allocated + bal.carried_forward} days available
                {bal.carried_forward > 0 && ` (+${bal.carried_forward} carried)`}
              </div>
              <div style={{
                marginTop: 8, height: 4, borderRadius: 2,
                background: "#f1f5f9", overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", background: bal.color,
                  width: `${Math.min(100, (bal.used / (bal.allocated + bal.carried_forward)) * 100)}%`,
                  transition: "width 0.3s",
                }} />
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                {bal.used} used · {bal.is_paid ? "Paid" : "Unpaid"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Apply Button */}
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: "#1e293b", fontSize: 18 }}>My Requests</h2>
        <button onClick={() => setShowForm(!showForm)} style={{
          background: "#1e3a8a",
          color: "white", border: "none", padding: "10px 20px",
          borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
        }}>
          {showForm ? "Cancel" : "+ Apply for Leave"}
        </button>
      </div>

      {/* Application Form */}
      {showForm && (
        <div style={{
          background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
          padding: 24, marginBottom: 20,
        }}>
          <h3 style={{ margin: "0 0 16px", color: "#1e3a8a", fontSize: 16 }}>New Leave Request</h3>
          {formError && (
            <div style={{ background: "#fee2e2", color: "#991b1b", padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
              {formError}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Leave Type *</label>
                <select value={form.leave_type_id} onChange={e => setForm(f => ({ ...f, leave_type_id: e.target.value }))}
                  style={inputStyle} required>
                  <option value="">Select type</option>
                  {types.map((t: any) => {
                    const bal = balances.find((b: any) => b.leave_type_id === t.id);
                    return (
                      <option key={t.id} value={t.id}>
                        {t.name} {bal ? `(${bal.available} days available)` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: "#475569" }}>
                  <input type="checkbox" checked={form.half_day}
                    onChange={e => setForm(f => ({ ...f, half_day: e.target.checked }))} />
                  Half day
                </label>
              </div>
              <div>
                <label style={labelStyle}>Start Date *</label>
                <DateInput value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  style={inputStyle} required />
              </div>
              <div>
                <label style={labelStyle}>End Date *</label>
                <DateInput value={form.end_date} min={form.start_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  style={inputStyle} required />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Reason</label>
              <textarea value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Optional reason for leave..."
                style={{ ...inputStyle, height: 80, resize: "vertical" }} />
            </div>
            <button type="submit" disabled={submitMut.isPending} style={{
              background: "#1e3a8a",
              color: "white", border: "none", padding: "10px 24px",
              borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
              opacity: submitMut.isPending ? 0.7 : 1,
            }}>
              {submitMut.isPending ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        </div>
      )}

      {/* Requests List */}
      {isLoading && <p style={{ color: "#64748b" }}>Loading...</p>}
      <div style={{ display: "grid", gap: 10 }}>
        {requests.map((req: any) => {
          const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
          return (
            <div key={req.id} className="leave-item" style={{
              background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
              padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{
                    display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                    background: req.leave_type_color || "#1e3a8a",
                  }} />
                  <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>{req.leave_type}</span>
                  <span style={{
                    background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                    padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  }}>{req.status}</span>
                </div>
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  {formatDate(req.start_date)} → {formatDate(req.end_date)}
                  <span style={{ marginLeft: 10 }}>{req.days} day{req.days !== 1 ? "s" : ""}{req.half_day ? " (half)" : ""}</span>
                </div>
                {req.reason && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{req.reason}</div>}
                {req.rejection_reason && (
                  <div style={{ fontSize: 12, color: "#dc2626", marginTop: 3 }}>
                    Reason: {req.rejection_reason}
                  </div>
                )}
              </div>
              {req.status === "pending" && (
                <button onClick={() => cancelMut.mutate(req.id)} disabled={cancelMut.isPending} style={{
                  background: "none", border: "1px solid #e2e8f0", color: "#64748b",
                  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12,
                }}>
                  Cancel
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!isLoading && requests.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ display: "block", margin: "0 auto" }}>
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <div style={{ fontSize: 15, marginTop: 12 }}>No leave requests yet.</div>
        </div>
      )}
      </div>}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
